import React, { useState } from 'react';
import { Users, Check, X, AlertCircle } from 'lucide-react';
import { DialogModal } from './DialogModal';
import { Button } from './ui/button';
import { format } from 'date-fns';

type PartnerLinkRequestModalProps = {
  open: boolean;
  onClose: () => void;
  clientProfile: ClientProfile;
  onAccept: () => Promise<void>;
  onDecline: () => Promise<void>;
  action: 'accept' | 'decline';
};

export function PartnerLinkRequestModal({
  open,
  onClose,
  clientProfile,
  onAccept,
  onDecline,
  action,
}: PartnerLinkRequestModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const requestingClient = clientProfile.partnerLinkRequestClient!;

  const handleAction = async () => {
    setIsProcessing(true);
    try {
      if (action === 'accept') {
        await onAccept();
      } else {
        await onDecline();
      }
      onClose();
    } catch (error) {
      console.error('Error processing partner link request:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const isAccept = action === 'accept';

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title={
        isAccept
          ? 'Accept Partner Link Request'
          : 'Decline Partner Link Request'
      }
      subTitle=""
      width="md"
      isLoading={isProcessing}
    >
      <div className="space-y-4 pt-4">
        {/* Request Details */}
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="flex items-center space-x-3">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-grow">
              <h3 className="font-medium text-gray-900">
                {requestingClient.clientFirstName}{' '}
                {requestingClient.clientLastName}
              </h3>
              <p className="text-sm text-gray-500">
                wants to link with {clientProfile.clientFirstName}{' '}
                {clientProfile.clientLastName}
              </p>
              <p className="text-xs text-gray-400">
                Requested on{' '}
                {format(clientProfile.clientAddedDateTime, 'MMMM d, yyyy')}
              </p>
            </div>
          </div>
        </div>

        {/* Action Explanation */}
        <div
          className={`rounded-lg p-4 ${isAccept ? 'bg-green-50' : 'bg-red-50'}`}
        >
          <div className="flex items-start space-x-3">
            {isAccept ? (
              <Check className="mt-0.5 h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="mt-0.5 h-5 w-5 text-red-600" />
            )}
            <div className="space-y-2">
              <h4
                className={`font-medium ${isAccept ? 'text-green-900' : 'text-red-900'}`}
              >
                {isAccept
                  ? 'Accepting this request will:'
                  : 'Declining this request will:'}
              </h4>
              <ul
                className={`space-y-1 text-sm ${isAccept ? 'text-green-800' : 'text-red-800'}`}
              >
                {isAccept ? (
                  <>
                    <li>• Link your accounts for joint planning</li>
                    <li>• Allow shared access to planning information</li>
                    <li>• Enable coordinated care planning calculations</li>
                  </>
                ) : (
                  <>
                    <li>• Permanently reject this link request</li>
                    <li>• Keep your accounts completely separate</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            disabled={isProcessing}
            className={`flex-1 ${
              isAccept
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {isProcessing ? (
              'Processing...'
            ) : (
              <>
                {isAccept ? (
                  <Check className="mr-2 h-4 w-4" />
                ) : (
                  <X className="mr-2 h-4 w-4" />
                )}
                {isAccept ? 'Accept Request' : 'Decline Request'}
              </>
            )}
          </Button>
        </div>
      </div>
    </DialogModal>
  );
}
